import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Loader2,
  UtensilsCrossed,
  Sparkles,
  Plus,
  Camera,
  CheckCircle2,
  ChevronUp,
  Search,
  ArrowUpDown,
  Filter,
  ChefHat,
  PackageOpen,
  ShieldAlert,
  Upload,
  Wine,
  Archive,
} from "lucide-react";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import api from "../lib/api";
import { useImageGeneration } from "../context/ImageGenerationContext";
import DishesGrid from "../components/recipes/DishesGrid";
import { RecipeDetailDialog } from "../components/recipes/RecipeDetailDialog";
import { ImportedDishesTab } from "../components/recipes/ImportedDishesTab";
import { BeveragesTab } from "../components/recipes/BeveragesTab";
import AsistenteIAFab from "../components/recipes/AsistenteIAFab";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "../components/ui/sheet";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "../components/ui/dropdown-menu";
import { MoreHorizontal } from "lucide-react";
import { NewBeverageDialog } from "../components/recipes/NewBeverageDialog";
import ArchivedDishesDialog from "../components/recipes/ArchivedDishesDialog";
import DuplicatesBanner from "../components/recipes/DuplicatesBanner";
import RecipesBaseTab from "../components/recipes/RecipesBaseTab";
import { AllergenReviewTab } from "../components/allergens/AllergenReviewTab";
import MenuImportDialog from "../components/MenuImportDialog";
import CreditCheckDialog from "../components/CreditCheckDialog";
import { PageHeader } from "../components/layout/PageHeader";
import { PageSkeleton } from "../components/layout/PageSkeleton";

export default function RecipesPage() {
  const { currentRestaurant } = useOutletContext();
  const {
    imgGenActive,
    restaurantBgPhotos,
    setRestaurantBgPhotos,
    handleGenerateDishImages,
    recipeGenActive,
    handleGenerateRecipes,
    imgBatchDoneCallback,
    recipeBatchDoneCallback,
  } = useImageGeneration();

  const [dishes, setDishes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingBg, setUploadingBg] = useState(false);
  const [importedCount, setImportedCount] = useState(0);
  const [activeTab, setActiveTab] = useState("recipes");
  // Bulk recipe generation mode — persisted in localStorage so the chef's
  // preference survives reloads.
  const [recipeBulkMode, setRecipeBulkMode] = useState(() => {
    try { return localStorage.getItem("mg_recipe_bulk_mode") || "simple"; } catch { return "simple"; }
  });
  useEffect(() => {
    try { localStorage.setItem("mg_recipe_bulk_mode", recipeBulkMode); } catch { /* noop */ }
  }, [recipeBulkMode]);

  // New recipe dialog
  const [newDishOpen, setNewDishOpen] = useState(false);
  const [newBeverageOpen, setNewBeverageOpen] = useState(false);
  const [beverageReloadKey, setBeverageReloadKey] = useState(0);
  const [newDishForm, setNewDishForm] = useState({ name: "", description: "", price: "", menuId: "", categoryId: "" });
  const [newDishCategories, setNewDishCategories] = useState([]);
  const [generatingDesc, setGeneratingDesc] = useState(false);
  const [creatingDish, setCreatingDish] = useState(false);
  const newDishCancelledRef = useRef(false);

  // Search / filter / sort
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [filterCategory, setFilterCategory] = useState("all");

  // Batch selection
  const [imgSelectionOpen, setImgSelectionOpen] = useState(false);
  const [recipeSelectionOpen, setRecipeSelectionOpen] = useState(false);
  const [selectedForImages, setSelectedForImages] = useState(new Set());
  const [selectedForRecipes, setSelectedForRecipes] = useState(new Set());

  // Recipe status
  const [dishesWithRecipesSet, setDishesWithRecipesSet] = useState(new Set());
  // Subset: dishes that have a PROFESSIONAL recipe (final_professional or
  // with base_recipe_refs). Used in bulk pro mode to distinguish refinable
  // legacy recipes from already-professional ones.
  const [dishesWithProRecipeSet, setDishesWithProRecipeSet] = useState(new Set());

  // Recipe dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedDish, setSelectedDish] = useState(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [creditCheck, setCreditCheck] = useState(null); // { action, count, label, onConfirm }

  // Archived dishes
  const [archivedOpen, setArchivedOpen] = useState(false);
  const [archivedOpenBeverages, setArchivedOpenBeverages] = useState(false);
  const [archivedCountRecipes, setArchivedCountRecipes] = useState(0);
  const [archivedCountBeverages, setArchivedCountBeverages] = useState(0);
  const [archivingDishId, setArchivingDishId] = useState(null);

  // Allergens pending count — surfaced as a status chip in the header
  // (the dedicated "Alérgenos" tab was removed in Feb 2026 UX refactor
  // because alergen review is an audit/status view, not a CRUD module).
  const [allergensPendingCount, setAllergensPendingCount] = useState(0);
  const [allergensSheetOpen, setAllergensSheetOpen] = useState(false);

  // Refs
  const bgPhotoInputRef = useRef(null);
  const recipeCheckCache = useRef({});

  // ── Data loading ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!currentRestaurant) return;
    fetchData();
    fetchImportedCount();
    fetchArchivedCounts();
    fetchAllergensPending();
    api.getBackgroundPhotos(currentRestaurant.restaurant_id)
      .then(res => setRestaurantBgPhotos(res.photos || []))
      .catch(() => setRestaurantBgPhotos([]));
  }, [currentRestaurant]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchImportedCount = async () => {
    if (!currentRestaurant) return;
    try {
      const data = await api.getImportedDishes(currentRestaurant.restaurant_id, "", "pending");
      setImportedCount(data.length);
    } catch { /* ignore */ }
  };

  const fetchAllergensPending = useCallback(async () => {
    if (!currentRestaurant) return;
    try {
      const r = await api.getAllergenReviews(currentRestaurant.restaurant_id, "pending");
      setAllergensPendingCount(Array.isArray(r) ? r.length : (r?.length ?? 0));
    } catch { /* silent */ }
  }, [currentRestaurant]);

  const fetchArchivedCounts = useCallback(async () => {
    if (!currentRestaurant) return;
    try {
      const all = await api.getAllDishesForRestaurantArchived(currentRestaurant.restaurant_id);
      // Split same way as fetchData: beverages vs recipes by cat/tag
      const BEV = ["bebida", "coctel", "cóctel", "refresco", "vino", "cerveza",
                   "café", "cafe", "té ", "licor", "carta de vinos", "bodega"];
      const menus = await api.getMenus(currentRestaurant.restaurant_id);
      const bevCatIds = new Set();
      for (const m of menus) {
        try {
          const cs = await api.getCategories(m.menu_id);
          cs.forEach((c) => {
            const n = (c.name || "").toLowerCase();
            if (BEV.some((k) => n.includes(k))) bevCatIds.add(c.category_id);
          });
        } catch { /* skip */ }
      }
      const isBev = (d) => d.source === "universal_beverage"
        || (d.tags || []).includes("bebida")
        || bevCatIds.has(d.category_id)
        || d.is_commercial_beverage === true;
      setArchivedCountBeverages(all.filter(isBev).length);
      setArchivedCountRecipes(all.filter((d) => !isBev(d)).length);
    } catch { /* non-critical */ }
  }, [currentRestaurant]);

  const handleArchiveDish = useCallback(async (dish) => {
    setArchivingDishId(dish.dish_id);
    try {
      await api.archiveDish(dish.dish_id);
      // Optimistic UI: remove from grid immediately.
      setDishes((prev) => prev.filter((d) => d.dish_id !== dish.dish_id));
      fetchArchivedCounts();
      setBeverageReloadKey((k) => k + 1);
      // Gmail-style toast with Undo (consistent with Elaboraciones Base).
      toast.success(`"${dish.name}" archivada`, {
        description: "Se moverá a Archivadas. Puedes restaurarla en cualquier momento.",
        duration: 5000,
        action: {
          label: "Deshacer",
          onClick: async () => {
            try {
              await api.restoreDish(dish.dish_id);
              // Put the dish back into the active grid without a full refetch.
              setDishes((prev) =>
                prev.some((d) => d.dish_id === dish.dish_id) ? prev : [...prev, dish],
              );
              fetchArchivedCounts();
              setBeverageReloadKey((k) => k + 1);
              toast.success("Restaurada");
            } catch (e) {
              toast.error(e?.message || "No se pudo deshacer");
            }
          },
        },
      });
    } catch (err) {
      toast.error(err.message || "No se pudo archivar");
    } finally {
      setArchivingDishId(null);
    }
  }, [fetchArchivedCounts]);

  // Register batch-done callbacks to refresh data & counters
  useEffect(() => {
    imgBatchDoneCallback.current = () => {
      recipeCheckCache.current = {};
      fetchData();
    };
    recipeBatchDoneCallback.current = () => {
      recipeCheckCache.current = {};
      fetchData();
    };
    return () => {
      imgBatchDoneCallback.current = null;
      recipeBatchDoneCallback.current = null;
    };
  }); // eslint-disable-line react-hooks/exhaustive-deps

  const checkRecipeStatus = async (dishList) => {
    if (!dishList || dishList.length === 0) return;
    const key = currentRestaurant?.restaurant_id;
    const now = Date.now();
    if (recipeCheckCache.current[key] && now - recipeCheckCache.current[key] < 30000) return;
    recipeCheckCache.current[key] = now;
    const results = await Promise.allSettled(dishList.map(d => api.getRecipe(d.dish_id)));
    const withRecipes = new Set();
    const withProRecipes = new Set();
    results.forEach((result, i) => {
      if (result.status === "fulfilled" && result.value && result.value.exists !== false) {
        withRecipes.add(dishList[i].dish_id);
        const r = result.value;
        if (r.recipe_type === "final_professional" || (r.base_recipe_refs || []).length > 0) {
          withProRecipes.add(dishList[i].dish_id);
        }
      }
    });
    setDishesWithRecipesSet(withRecipes);
    setDishesWithProRecipeSet(withProRecipes);
  };

  const fetchData = async () => {
    try {
      const menusData = await api.getMenus(currentRestaurant.restaurant_id);
      setMenus(menusData);
      const allDishes = [];
      const allCategories = new Map();
      for (const menu of menusData) {
        try {
          const menuDishes = await api.getDishes(menu.menu_id);
          allDishes.push(...menuDishes);
        } catch (e) { /* silenced */ }
        try {
          const menuCategories = await api.getCategories(menu.menu_id);
          menuCategories.forEach(cat => allCategories.set(cat.category_id, cat));
        } catch (e) { /* silenced */ }
      }
      const BEV_CAT_KEYWORDS = ["bebida", "coctel", "cóctel", "refresco", "vino", "cerveza", "café", "cafe", "té ", "licor", "carta de vinos", "bodega"];
      const beverageCatIds = new Set(
        Array.from(allCategories.values())
          .filter(c => {
            const n = (c.name || "").toLowerCase();
            return BEV_CAT_KEYWORDS.some(k => n.includes(k));
          })
          .map(c => c.category_id)
      );
      setDishes(allDishes.filter(d =>
        d.source !== "universal_beverage" &&
        !(d.tags || []).includes("bebida") &&
        !beverageCatIds.has(d.category_id) &&
        d.is_commercial_beverage !== true
      ));
      setCategories(Array.from(allCategories.values()));
      checkRecipeStatus(allDishes);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  // ── New dish (recipe) creation ──────────────────────────────────────────
  const handleMenuChangeForNewDish = useCallback(async (menuId) => {
    setNewDishForm(prev => ({ ...prev, menuId, categoryId: "" }));
    try {
      const cats = await api.getCategories(menuId);
      setNewDishCategories(cats);
    } catch {
      setNewDishCategories([]);
    }
  }, []);

  const handleGenerateDescription = async () => {
    if (!newDishForm.name.trim()) { toast.error("Introduce el nombre del plato primero"); return; }
    setGeneratingDesc(true);
    newDishCancelledRef.current = false;
    try {
      const result = await api.aiGenerateRecipe(
        { dish_name: newDishForm.name, servings: 4 },
        currentRestaurant?.restaurant_id,
      );
      if (newDishCancelledRef.current) return;
      const desc = result.description
        || (result.steps?.length ? result.steps[0] : null)
        || (result.ingredients?.length
          ? `Plato elaborado con ${result.ingredients.slice(0, 3).map(i => i.name).join(", ")} y más ingredientes.`
          : `Delicioso plato de ${newDishForm.name}.`);
      setNewDishForm(prev => ({ ...prev, description: desc }));
      toast.success("Descripción generada con IA");
    } catch { if (!newDishCancelledRef.current) toast.error("Error al generar descripción"); }
    finally { if (!newDishCancelledRef.current) setGeneratingDesc(false); }
  };

  const handleCreateNewDish = async (e) => {
    e.preventDefault();
    if (!newDishForm.name.trim()) { toast.error("El nombre es obligatorio"); return; }
    if (!newDishForm.menuId) { toast.error("Selecciona un menú para el plato"); return; }
    setCreatingDish(true);
    try {
      const newDish = await api.createDish(newDishForm.menuId, {
        name: newDishForm.name.trim(),
        description: newDishForm.description.trim() || undefined,
        price: newDishForm.price ? parseFloat(newDishForm.price) : 0,
        category_id: newDishForm.categoryId || undefined,
        is_active: true,
        allergens: [],
        tags: [],
      });
      setDishes(prev => [newDish, ...prev]);
      setNewDishOpen(false);
      setNewDishForm({ name: "", description: "", price: "", menuId: "", categoryId: "" });
      setNewDishCategories([]);
      toast.success(`"${newDish.name}" creado. Ya puedes generar su imagen y receta.`);
    } catch (err) {
      toast.error(err.message || "Error al crear el plato");
    } finally {
      setCreatingDish(false);
    }
  };

  // ── Dish updated callback (from dialog — syncs grid + selected dish) ────
  const handleDishUpdated = useCallback((dishId, updates) => {
    setDishes(prev => prev.map(d => d.dish_id === dishId ? { ...d, ...updates } : d));
    setSelectedDish(prev => prev?.dish_id === dishId ? { ...prev, ...updates } : prev);
  }, []);

  // ── Background photos ───────────────────────────────────────────────────
  const handleUploadBgPhoto = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const oversized = files.find(f => f.size > 5 * 1024 * 1024);
    if (oversized) { toast.error("Alguna imagen supera los 5MB."); return; }
    setUploadingBg(true);
    try {
      const newUrls = [];
      for (const file of files) {
        const result = await api.uploadImage(file);
        if (result.success) newUrls.push(result.image_url);
      }
      if (newUrls.length > 0) {
        const updated = [...restaurantBgPhotos, ...newUrls];
        setRestaurantBgPhotos(updated);
        await api.updateBackgroundPhotos(currentRestaurant.restaurant_id, updated);
        toast.success(`${newUrls.length} foto${newUrls.length > 1 ? "s" : ""} guardada${newUrls.length > 1 ? "s" : ""}`);
      }
    } catch { toast.error("Error al subir las fotos"); }
    finally {
      setUploadingBg(false);
      if (bgPhotoInputRef.current) bgPhotoInputRef.current.value = "";
    }
  };

  const handleRemoveBgPhoto = async (idx) => {
    const updated = restaurantBgPhotos.filter((_, i) => i !== idx);
    setRestaurantBgPhotos(updated);
    try { await api.updateBackgroundPhotos(currentRestaurant.restaurant_id, updated); }
    catch { toast.error("Error al eliminar la foto"); }
  };

  // ── Computed values ─────────────────────────────────────────────────────
  const filteredAndSortedDishes = useMemo(() => {
    let result = [...dishes];
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(d =>
        d.name?.toLowerCase().includes(query) || d.description?.toLowerCase().includes(query)
      );
    }
    if (filterCategory !== "all") result = result.filter(d => d.category_id === filterCategory);
    result.sort((a, b) => {
      switch (sortBy) {
        case "name":        return (a.name || "").localeCompare(b.name || "", "es");
        case "name_desc":   return (b.name || "").localeCompare(a.name || "", "es");
        case "created":     return new Date(b.created_at || 0) - new Date(a.created_at || 0);
        case "created_asc": return new Date(a.created_at || 0) - new Date(b.created_at || 0);
        case "category":    {
          const catA = categories.find(c => c.category_id === a.category_id)?.name || "";
          const catB = categories.find(c => c.category_id === b.category_id)?.name || "";
          return catA.localeCompare(catB, "es");
        }
        case "price":       return (b.price || 0) - (a.price || 0);
        default:            return 0;
      }
    });
    return result;
  }, [dishes, searchQuery, sortBy, filterCategory, categories]);

  const dishesWithoutImages = useMemo(() => dishes.filter(d => !d.image_url).length, [dishes]);
  const dishesWithoutRecipes = useMemo(
    () => dishes.filter(d => !dishesWithRecipesSet.has(d.dish_id)).length,
    [dishes, dishesWithRecipesSet]
  );

  const getCategoryName = useCallback((categoryId) => {
    return categories.find(c => c.category_id === categoryId)?.name || "Sin categoría";
  }, [categories]);

  const onClearFilters = useCallback(() => {
    setSearchQuery("");
    setFilterCategory("all");
  }, []);

  const openRecipeDialog = useCallback((dish) => {
    setSelectedDish(dish);
    setDialogOpen(true);
  }, []);

  if (loading) {
    return <PageSkeleton kpis={0} showTabs rows={6} />;
  }

  return (
    <div className="space-y-6" data-testid="recipes-page">
      {/* Header */}
      <PageHeader
        icon={<UtensilsCrossed className="w-5 h-5" />}
        title="Recetas"
        description="Genera, edita y gestiona las recetas de tus platos."
        breadcrumb={[{ label: "Carta y cocina" }, { label: "Recetas" }]}
        actions={
          <>
            {/* Allergen audit status chip — Feb 2026: replaces dedicated tab.
                Only renders when there are pending reviews so a clean state
                hides the chip entirely (no visual noise). */}
            {allergensPendingCount > 0 && (
              <Button
                variant="outline"
                onClick={() => setAllergensSheetOpen(true)}
                className="gap-2 border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100 dark:bg-amber-950/30 dark:border-amber-900/40 dark:text-amber-300"
                data-testid="open-allergens-chip"
              >
                <ShieldAlert className="w-4 h-4" />
                <span className="hidden sm:inline">Alérgenos</span>
                <span className="font-bold tabular-nums">{allergensPendingCount}</span>
              </Button>
            )}
            <Button
              onClick={() => setNewDishOpen(true)}
              className="gap-2"
              data-testid="new-recipe-btn"
            >
              <Plus className="w-4 h-4" />
              Nueva receta
            </Button>
            <Button
              onClick={() => setNewBeverageOpen(true)}
              variant="secondary"
              className="gap-2"
              data-testid="new-beverage-btn"
            >
              <Wine className="w-4 h-4" />
              Nueva bebida
            </Button>
            {/* Secondary actions menu — Importar lives here so the primary
                row stays focused on the 2 most-used CTAs. */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="recipes-more-menu" aria-label="Más acciones de recetas">
                  <MoreHorizontal className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuItem
                  onClick={() => setImportDialogOpen(true)}
                  data-testid="import-recipes-menuitem"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Importar recetas
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setAllergensSheetOpen(true)}
                  data-testid="open-allergens-menuitem"
                >
                  <ShieldAlert className="w-4 h-4 mr-2" />
                  Auditoría de alérgenos
                  {allergensPendingCount > 0 && (
                    <span className="ml-auto text-[10px] font-bold tabular-nums bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">
                      {allergensPendingCount}
                    </span>
                  )}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        }
      />

      {/* Tabs — modernized: larger touch targets (h-12 = 48 px), pill style,
          scrollable on narrow viewports. Alérgenos extracted to header chip. */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList
          className="h-12 p-1 bg-muted/60 rounded-xl w-full sm:w-auto overflow-x-auto flex-nowrap justify-start sm:justify-center gap-1"
          data-testid="recipes-tabs"
        >
          <TabsTrigger
            value="recipes"
            data-testid="tab-recipes"
            className="h-10 px-4 gap-2 rounded-lg data-[state=active]:bg-card data-[state=active]:shadow-sm whitespace-nowrap text-sm"
          >
            <ChefHat className="w-4 h-4" /> Recetas
          </TabsTrigger>
          <TabsTrigger
            value="base"
            data-testid="tab-base"
            className="h-10 px-4 gap-2 rounded-lg data-[state=active]:bg-card data-[state=active]:shadow-sm whitespace-nowrap text-sm"
          >
            <Sparkles className="w-4 h-4" /> Elaboraciones Base
          </TabsTrigger>
          <TabsTrigger
            value="beverages"
            data-testid="tab-beverages"
            className="h-10 px-4 gap-2 rounded-lg data-[state=active]:bg-card data-[state=active]:shadow-sm whitespace-nowrap text-sm"
          >
            <Wine className="w-4 h-4" /> Bebidas
          </TabsTrigger>
          <TabsTrigger
            value="imported"
            data-testid="tab-imported"
            className="h-10 px-4 gap-2 rounded-lg data-[state=active]:bg-card data-[state=active]:shadow-sm whitespace-nowrap text-sm"
          >
            <PackageOpen className="w-4 h-4" /> Platos Importados
            {importedCount > 0 && (
              <Badge variant="secondary" className="ml-0.5 text-[10px] px-1.5 py-0 bg-amber-100 text-amber-700 border-amber-200">
                {importedCount}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="base">
          <RecipesBaseTab
            restaurantId={currentRestaurant?.restaurant_id}
          />
        </TabsContent>

        <TabsContent value="beverages">
          <BeveragesTab
            restaurantId={currentRestaurant?.restaurant_id}
            onViewRecipe={openRecipeDialog}
            reloadKey={beverageReloadKey}
            archivedCount={archivedCountBeverages}
            onOpenArchived={() => setArchivedOpenBeverages(true)}
            onArchive={() => { fetchArchivedCounts(); }}
          />
        </TabsContent>

        <TabsContent value="imported">
          <ImportedDishesTab
            restaurantId={currentRestaurant?.restaurant_id}
            onIncorporated={() => { fetchData(); fetchImportedCount(); }}
          />
        </TabsContent>

        {/* Allergen tab removed (Feb 2026) — content now lives in a Sheet
            triggered by the status chip + "···" menu in the header. */}

        <TabsContent value="recipes" className="space-y-6">

      {/* ── Duplicate names banner (non-archived dishes only) ──────────── */}
      <DuplicatesBanner
        dishes={dishes}
        onOpenDish={openRecipeDialog}
        onArchiveDish={handleArchiveDish}
        busyId={archivingDishId}
      />

      {/* Batch Generation — Feb 2026: replaced the two large cards with a
          floating "Asistente IA" button (visible on every tab) that opens a
          Sheet drawer. Frees ~40% of viewport for the dish grid itself and
          guarantees ≥48 px touch targets for the IA actions. */}


      {/* Search and Filter Bar */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por nombre o descripción..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="recipe-search-input"
              />
            </div>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-full sm:w-[200px]" data-testid="recipe-sort-select" aria-label="Ordenar recetas">
                <ArrowUpDown className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Ordenar por..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="name">A-Z (Alfabético)</SelectItem>
                <SelectItem value="name_desc">Z-A (Alfabético inverso)</SelectItem>
                <SelectItem value="created">Más recientes primero</SelectItem>
                <SelectItem value="created_asc">Más antiguos primero</SelectItem>
                <SelectItem value="category">Por categoría</SelectItem>
                <SelectItem value="price">Por precio (mayor a menor)</SelectItem>
              </SelectContent>
            </Select>
            {categories.length > 0 && (
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="w-full sm:w-[180px]" data-testid="recipe-category-filter" aria-label="Filtrar recetas por categoría">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Categoría" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las categorías</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat.category_id} value={cat.category_id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <div className="mt-3 flex items-center justify-between gap-2">
            <div className="text-sm text-muted-foreground">
              {filteredAndSortedDishes.length === dishes.length ? (
                <span>{dishes.length} platos en total</span>
              ) : (
                <span>Mostrando {filteredAndSortedDishes.length} de {dishes.length} platos
                  {searchQuery && <span className="ml-1">para "{searchQuery}"</span>}
                </span>
              )}
            </div>
            {archivedCountRecipes > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="text-xs gap-1.5 text-muted-foreground hover:text-foreground"
                onClick={() => setArchivedOpen(true)}
                data-testid="open-archived-recipes-btn"
              >
                <Archive className="w-3.5 h-3.5" />
                Ver archivadas ({archivedCountRecipes})
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Dishes Grid */}
      <DishesGrid
        dishes={dishes}
        filteredDishes={filteredAndSortedDishes}
        searchQuery={searchQuery}
        onViewRecipe={openRecipeDialog}
        onArchive={handleArchiveDish}
        onClearFilters={onClearFilters}
        dishesWithRecipesSet={dishesWithRecipesSet}
        busyId={archivingDishId}
      />

        </TabsContent>
      </Tabs>

      {/* ── Floating "Asistente IA" — Feb 2026 ──────────────────────────
          Always visible (any tab), opens a Sheet drawer with both batch
          actions. Replaces the two large cards that used to take 40% of
          the viewport above the dish grid. */}
      <AsistenteIAFab
        allDishes={dishes}
        dishesWithoutImages={dishesWithoutImages}
        dishesWithoutRecipes={dishesWithoutRecipes}
        dishesWithRecipesSet={dishesWithRecipesSet}
        dishesWithProRecipeSet={dishesWithProRecipeSet}
        selectedForImages={selectedForImages}
        setSelectedForImages={setSelectedForImages}
        selectedForRecipes={selectedForRecipes}
        setSelectedForRecipes={setSelectedForRecipes}
        imgGenActive={imgGenActive}
        recipeGenActive={recipeGenActive}
        recipeBulkMode={recipeBulkMode}
        setRecipeBulkMode={setRecipeBulkMode}
        restaurantBgPhotos={restaurantBgPhotos}
        onUploadBgPhoto={handleUploadBgPhoto}
        onRemoveBgPhoto={handleRemoveBgPhoto}
        uploadingBg={uploadingBg}
        bgPhotoInputRef={bgPhotoInputRef}
        onGenerateImages={(selected) => {
          const count = selected.size > 0
            ? selected.size
            : (dishes.filter(d => !d.image_url).length || dishes.length);
          setCreditCheck({
            action: "image_gen_dish",
            count,
            label: "imagenes",
            onConfirm: () => {
              setCreditCheck(null);
              handleGenerateDishImages(
                currentRestaurant, restaurantBgPhotos,
                selected.size > 0 ? [...selected] : null,
              );
            },
          });
        }}
        onGenerateRecipes={(selected, { mode }) => {
          const eligibleForPro = dishes.filter(d => !dishesWithProRecipeSet.has(d.dish_id));
          const eligibleForSimple = dishes.filter(d => !dishesWithRecipesSet.has(d.dish_id));
          const baseList = mode === "professional" ? eligibleForPro : eligibleForSimple;
          const count = selected.size > 0 ? selected.size : (baseList.length || dishes.length);
          setCreditCheck({
            action: "recipe_gen",
            count,
            label: mode === "professional" ? "recetas profesionales" : "recetas",
            onConfirm: () => {
              setCreditCheck(null);
              handleGenerateRecipes(
                currentRestaurant,
                selected.size > 0 ? [...selected] : null,
                { mode },
              );
            },
          });
        }}
      />

      {/* ── Allergen audit Sheet — Feb 2026 ─────────────────────────────
          Triggered by the status chip in the header (visible only when
          there are pending reviews) and by the "···" menu (always). */}
      <Sheet
        open={allergensSheetOpen}
        onOpenChange={(o) => {
          setAllergensSheetOpen(o);
          // Refresh the pending count when the sheet closes so the header
          // chip / "···" menu reflect any approvals/rejections done inside.
          if (!o) fetchAllergensPending();
        }}
      >
        <SheetContent
          side="right"
          className="w-full sm:max-w-2xl overflow-y-auto"
          data-testid="allergens-sheet"
        >
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-amber-700" />
              Auditoría de alérgenos
            </SheetTitle>
            <SheetDescription>
              Revisa y aprueba la información de alérgenos detectada por la IA en tus platos.
            </SheetDescription>
          </SheetHeader>
          <div className="mt-4">
            <AllergenReviewTab
              restaurantId={currentRestaurant?.restaurant_id}
            />
          </div>
        </SheetContent>
      </Sheet>

      {/* Recipe / Beverage Detail Dialog — rendered at page root so it's
          visible regardless of the currently active tab */}
      <RecipeDetailDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        selectedDish={selectedDish}
        onDishUpdated={handleDishUpdated}
        isBeverage={selectedDish?._isBeverage === true}
        restaurantId={currentRestaurant?.restaurant_id}
      />

      {/* Archived recipes dialog */}
      <ArchivedDishesDialog
        open={archivedOpen}
        onOpenChange={setArchivedOpen}
        restaurantId={currentRestaurant?.restaurant_id}
        isBeverage={false}
        onChanged={() => { fetchArchivedCounts(); fetchData(); }}
      />

      {/* Archived beverages dialog */}
      <ArchivedDishesDialog
        open={archivedOpenBeverages}
        onOpenChange={setArchivedOpenBeverages}
        restaurantId={currentRestaurant?.restaurant_id}
        isBeverage={true}
        onChanged={() => { fetchArchivedCounts(); setBeverageReloadKey((k) => k + 1); }}
      />

      {/* New Beverage Dialog (scan barcode / manual) */}
      <NewBeverageDialog
        open={newBeverageOpen}
        onOpenChange={setNewBeverageOpen}
        restaurantId={currentRestaurant?.restaurant_id}
        onBeverageCreated={() => {
          // Switch to Beverages tab and trigger a refresh
          setActiveTab("beverages");
          setBeverageReloadKey(k => k + 1);
        }}
      />

      {/* New Recipe Dialog */}
      <Dialog open={newDishOpen} onOpenChange={(open) => {
        if (!open) {
          newDishCancelledRef.current = true;
          setGeneratingDesc(false);
          setNewDishForm({ name: "", description: "", price: "", menuId: "", categoryId: "" });
          setNewDishCategories([]);
        }
        setNewDishOpen(open);
      }}>
        <DialogContent className="sm:max-w-[500px]" data-testid="new-recipe-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ChefHat className="w-5 h-5 text-primary" />
              Nueva receta
            </DialogTitle>
            <DialogDescription>
              Crea un nuevo plato. Después podrás generar su imagen y receta con IA.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleCreateNewDish} className="space-y-4 pt-2">
            {/* Dish name */}
            <div className="space-y-1.5">
              <Label htmlFor="new-dish-name">
                Nombre del plato <span className="text-destructive">*</span>
              </Label>
              <Input
                id="new-dish-name"
                placeholder="Ej: Paella valenciana"
                value={newDishForm.name}
                onChange={(e) => setNewDishForm(prev => ({ ...prev, name: e.target.value }))}
                data-testid="new-dish-name-input"
                autoFocus
              />
            </div>

            {/* Menu selector (optional) */}
            <div className="space-y-1.5">
              <Label htmlFor="new-dish-menu">
                Menú <span className="text-destructive">*</span>
              </Label>
              <Select
                value={newDishForm.menuId}
                onValueChange={handleMenuChangeForNewDish}
              >
                <SelectTrigger id="new-dish-menu" data-testid="new-dish-menu-select">
                  <SelectValue placeholder="Seleccionar menú..." />
                </SelectTrigger>
                <SelectContent>
                  {menus.map(menu => (
                    <SelectItem key={menu.menu_id} value={menu.menu_id}>
                      {menu.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Category selector (optional, depends on menu) */}
            {newDishForm.menuId && (
              <div className="space-y-1.5">
                <Label htmlFor="new-dish-category">Categoría (opcional)</Label>
                <Select
                  value={newDishForm.categoryId}
                  onValueChange={(val) => setNewDishForm(prev => ({ ...prev, categoryId: val }))}
                  disabled={newDishCategories.length === 0}
                >
                  <SelectTrigger id="new-dish-category" data-testid="new-dish-category-select">
                    <SelectValue placeholder={newDishCategories.length === 0 ? "Sin categorías disponibles" : "Seleccionar categoría..."} />
                  </SelectTrigger>
                  <SelectContent>
                    {newDishCategories.map(cat => (
                      <SelectItem key={cat.category_id} value={cat.category_id}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Price (optional) */}
            <div className="space-y-1.5">
              <Label htmlFor="new-dish-price">Precio (opcional)</Label>
              <Input
                id="new-dish-price"
                type="number"
                min="0"
                step="0.01"
                placeholder="Ej: 12.50"
                value={newDishForm.price}
                onChange={(e) => setNewDishForm(prev => ({ ...prev, price: e.target.value }))}
                data-testid="new-dish-price-input"
              />
            </div>

            {/* Description with AI generation */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="new-dish-desc">Descripción (opcional)</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs gap-1 text-primary hover:text-primary/80 px-2"
                  onClick={handleGenerateDescription}
                  disabled={generatingDesc || !newDishForm.name.trim()}
                  data-testid="generate-desc-btn"
                >
                  {generatingDesc
                    ? <><Loader2 className="w-3 h-3 animate-spin" />Generando...</>
                    : <><Sparkles className="w-3 h-3" />Generar con IA</>
                  }
                </Button>
              </div>
              <Textarea
                id="new-dish-desc"
                placeholder="Descripción del plato para el menú público..."
                value={newDishForm.description}
                onChange={(e) => setNewDishForm(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
                data-testid="new-dish-description-textarea"
              />
            </div>

            <DialogFooter className="gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  newDishCancelledRef.current = true;
                  setGeneratingDesc(false);
                  setNewDishOpen(false);
                  setNewDishForm({ name: "", description: "", price: "", menuId: "", categoryId: "" });
                  setNewDishCategories([]);
                }}
                data-testid="new-dish-cancel-btn"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={creatingDish || !newDishForm.name.trim()}
                data-testid="new-dish-submit-btn"
              >
                {creatingDish
                  ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Creando...</>
                  : <><Plus className="w-4 h-4 mr-2" />Crear plato</>
                }
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Menu Import Dialog */}
      <MenuImportDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        restaurantId={currentRestaurant?.restaurant_id}
        onImported={() => { setImportDialogOpen(false); fetchData(); }}
      />

      {/* Credit Check Dialog */}
      {creditCheck && (
        <CreditCheckDialog
          open={!!creditCheck}
          onClose={() => setCreditCheck(null)}
          onConfirm={creditCheck.onConfirm}
          restaurantId={currentRestaurant?.restaurant_id}
          action={creditCheck.action}
          count={creditCheck.count}
          actionLabel={creditCheck.label}
        />
      )}
    </div>
  );
}
